<template>
  <!-- Use the Preloader component here -->
  <Preloader :isVisible="isPreloaderVisible" />
  <!-- Include the HeaderTop component -->
  <Header />

  <!-- Main app content -->
  <FreePopup />
  <div class="main-body">
    <Sidebar />

    <!--Middle Body-->
    <div class="body-middle">
      <router-view :key="$route.fullPath" />

      <MobileNav />

      <Footer />
    </div>
    <!--Middle Body-->

    <Chatbar 
    v-if="$route.name !== 'game.show' && chatEnabled"
    />
  </div>
</template>

<script>
import { ref, onMounted } from "vue";

import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import $ from 'jquery';
window.$ = window.jQuery = $;  // Make jQuery available globally

import Header from "@/components/layout/Header.vue";
import Preloader from "@/components/partials/Preloader.vue";
import Sidebar from "@/components/layout/Sidebar.vue";
import Chatbar from "@/components/layout/Chatbar.vue";
import Footer from "@/components/layout/Footer.vue";
import MobileNav from "@/components/layout/MobileNav.vue";
import { useAuthStore } from '@/store/auth'
import { eventBus } from '@/eventBus';
import { mapActions } from "pinia";
import helpers from "@/helpers";
import FreePopup from "@/components/layout/FreePopup.vue";
import { useHomeStore } from '@/store/home'
import cache from '@/cache';

export default {
  name: "App",
  inject: ["isRtlLayout"],
  data() {
    return {
      isLoading: false,
      chatEnabled: true,
      updateLastPlayIntervalId: null,
      loadingHandler: null, // Store reference for proper cleanup
      visibilityHandler: null, // Store reference for visibility change handler
    };
  },
  components: {
    Header,
    Preloader,
    Sidebar,
    Chatbar,
    Footer,
    MobileNav,
    FreePopup,
  },
  setup() {
    const isPreloaderVisible = ref(true);

    onMounted(() => {
      // Clear expired cache entries on app start
      cache.clearExpired();
      
      // Hide preloader faster - don't wait for full window load
      // Use requestIdleCallback for better performance, fallback to setTimeout
      const hidePreloader = () => {
        // Shorter timeout for faster perceived load
        setTimeout(() => {
          isPreloaderVisible.value = false;
        }, 500);
      };
      
      if ('requestIdleCallback' in window) {
        requestIdleCallback(hidePreloader, { timeout: 1000 });
      } else {
        // Fallback for Safari and older browsers
        setTimeout(hidePreloader, 300);
      }
    });

    return {
      isPreloaderVisible,
    };
  },
  methods: {
    ...mapActions(useAuthStore, ["autoLogin", "clearData", "updateLastPlay"]),
    ...mapActions(useHomeStore, ['fetchConfigData']),
    startUpdateInterval() {
      if (this.updateLastPlayIntervalId) return; // Already running
      this.updateLastPlayIntervalId = setInterval(async () => {
        await this.updateLastPlay();
      }, 30000);
    },
    stopUpdateInterval() {
      if (this.updateLastPlayIntervalId) {
        clearInterval(this.updateLastPlayIntervalId);
        this.updateLastPlayIntervalId = null;
      }
    },
  },
  async created() {
    this.chatEnabled = helpers.getDomainConfig().chatEnabled;

    // Store handler reference for proper cleanup
    this.loadingHandler = (loading) => {
      this.isLoading = loading;
    };
    eventBus.on('loading', this.loadingHandler);

    const configStatus = await this.fetchConfigData();
    if (configStatus == "config-exist" || configStatus == "config-loaded") {
      if (configStatus == "config-loaded") {
        location.reload();
      }
      const phone = localStorage.getItem("phone");

      if (phone) {
        // Don't await autoLogin - let it run in background
        this.autoLogin(phone).catch(console.error);
      } else {
        this.clearData();
      }

      this.startUpdateInterval();
      
      // Pause/resume interval when tab visibility changes to save resources
      this.visibilityHandler = () => {
        if (document.hidden) {
          this.stopUpdateInterval();
        } else {
          this.startUpdateInterval();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    } else {
      // Show error page...
      helpers.showToast(
        this.$t("Sorry this system is not configured, please contact admin"),
        this.$t("Error"),
        "error"
      );
    }
  },
  beforeUnmount() {
    // Clean up the interval to prevent memory leaks
    this.stopUpdateInterval();
    // Clean up event bus listener (only this handler, not all listeners)
    if (this.loadingHandler) {
      eventBus.off('loading', this.loadingHandler);
      this.loadingHandler = null;
    }
    // Clean up visibility change listener
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  },
};
</script>

<style>
body {
  margin: 0;
  font-family: Arial, sans-serif;
}
</style>
